import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabsV2, TabV2ButtonItem, TabV2Item } from 'common/ui/TabsV2';
import { useSyncUrlParamToStore } from 'common/hooks';
import {
  CandidateListTab,
  useCandidateListStore
} from 'modules/candidate-profile/stores/useCandidateListStore';
import { CreateTalentPoolButton } from 'modules/candidate-profile/forms/components';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { getShouldShowCandidateProfileListTabs } from 'modules/candidate-profile/utils/getShouldShowCandidateProfileListTabs';
import { usePreselectedWorkflowId } from 'modules/candidate-profile/hooks/usePreselectedWorkflowId';

type CandidateProfileListTabsProps = {
  candidateProfileListProps: CandidateProfileListProps;
};

const CANDIDATE_LIST_TAB_VALUES = Object.values(CandidateListTab);
const CANDIDATES_ONLY_TAB_VALUES = [CandidateListTab.CANDIDATES];

export const CandidateProfileListTabs: React.FC<
  CandidateProfileListTabsProps
> = ({ candidateProfileListProps }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeTab,
    totalCandidatesCount,
    talentListsCount,
    isCandidatesCountLoading,
    setActiveTab
  } = useCandidateListStore();

  // Handed over from the builder to populate one workflow. Talent lists cannot accept that
  // push, so offering them here is only a way to get lost.
  const isWorkflowHandover = Boolean(usePreselectedWorkflowId());

  useSyncUrlParamToStore({
    paramKey: 'tab',
    validValues: isWorkflowHandover
      ? CANDIDATES_ONLY_TAB_VALUES
      : CANDIDATE_LIST_TAB_VALUES,
    setStoreValue: setActiveTab
  });

  // The panel below is chosen from activeTab alone, so a tab we no longer render would show
  // a talent list with nothing to click back from. Nothing can leave the store in that state
  // today, but the screen shouldn't depend on that staying true.
  useEffect(() => {
    if (isWorkflowHandover && activeTab !== CandidateListTab.CANDIDATES) {
      setActiveTab(CandidateListTab.CANDIDATES);
    }
  }, [isWorkflowHandover, activeTab, setActiveTab]);

  const tabs: TabV2Item[] = useMemo(
    () => [
      {
        label: 'Candidates',
        value: CandidateListTab.CANDIDATES,
        // No badge when the search found no one.
        count: totalCandidatesCount || undefined,
        isCountLoading: isCandidatesCountLoading
      },
      ...(isWorkflowHandover
        ? []
        : [
            {
              label: 'Talent lists',
              value: CandidateListTab.TALENT_LISTS,
              count: talentListsCount ?? undefined
            }
          ])
    ],
    [
      totalCandidatesCount,
      talentListsCount,
      isCandidatesCountLoading,
      isWorkflowHandover
    ]
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value as CandidateListTab);
    // Update URL to keep in sync
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams, { replace: true });
  };

  const getShouldShowCreateTalentPoolButton = () => {
    if (activeTab !== CandidateListTab.TALENT_LISTS) return false;
    if (
      candidateProfileListProps.type ===
      CandidateProfileListType.ANALYSIS_CANDIDATES_LIST
    )
      return false;
    return true;
  };

  const shouldShowCreateTalentPoolButton =
    getShouldShowCreateTalentPoolButton();

  const endItems: TabV2ButtonItem[] | undefined =
    shouldShowCreateTalentPoolButton ? [<CreateTalentPoolButton />] : undefined;

  const shouldShowCandidateProfileListTabs =
    getShouldShowCandidateProfileListTabs({ candidateProfileListProps });

  if (!shouldShowCandidateProfileListTabs) return null;

  return (
    <TabsV2
      tabs={tabs}
      value={activeTab}
      onChange={handleTabChange}
      endItems={endItems}
    />
  );
};
